"use client";

import type React from "react";

import { useState } from "react";
import { useTranslation } from "../../../i18n";
import {
  CheckCircle,
  ArrowRight,
  Plus,
  Settings,
  Smartphone,
  Eye,
} from "lucide-react";

interface QuickStartStep {
  id: number;
  title: string;
  description: string;
  action: string;
  tips: string[];
  icon: React.ReactNode;
}

export default function DigitalTwinsQuickStart() {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);

  const quickStartSteps: QuickStartStep[] = [
    {
      id: 1,
      title: t('routes.digitalTwins.quickStart.steps.0.title'),
      description: t('routes.digitalTwins.quickStart.steps.0.description'),
      action: t('routes.digitalTwins.quickStart.steps.0.action'),
      tips: [
        t('routes.digitalTwins.quickStart.steps.0.tips.0'),
        t('routes.digitalTwins.quickStart.steps.0.tips.1'),
        t('routes.digitalTwins.quickStart.steps.0.tips.2'),
      ],
      icon: <Plus className="h-5 w-5" />,
    },
    {
      id: 2,
      title: t('routes.digitalTwins.quickStart.steps.1.title'),
      description: t('routes.digitalTwins.quickStart.steps.1.description'),
      action: t('routes.digitalTwins.quickStart.steps.1.action'),
      tips: [
        t('routes.digitalTwins.quickStart.steps.1.tips.0'),
        t('routes.digitalTwins.quickStart.steps.1.tips.1'),
        t('routes.digitalTwins.quickStart.steps.1.tips.2'),
      ],
      icon: <Settings className="h-5 w-5" />,
    },
    {
      id: 3,
      title: t('routes.digitalTwins.quickStart.steps.2.title'),
      description: t('routes.digitalTwins.quickStart.steps.2.description'),
      action: t('routes.digitalTwins.quickStart.steps.2.action'),
      tips: [
        t('routes.digitalTwins.quickStart.steps.2.tips.0'),
        t('routes.digitalTwins.quickStart.steps.2.tips.1'),
        t('routes.digitalTwins.quickStart.steps.2.tips.2'),
      ],
      icon: <Smartphone className="h-5 w-5" />,
    },
    {
      id: 4,
      title: t('routes.digitalTwins.quickStart.steps.3.title'),
      description: t('routes.digitalTwins.quickStart.steps.3.description'),
      action: t('routes.digitalTwins.quickStart.steps.3.action'),
      tips: [
        t('routes.digitalTwins.quickStart.steps.3.tips.0'),
        t('routes.digitalTwins.quickStart.steps.3.tips.1'),
        t('routes.digitalTwins.quickStart.steps.3.tips.2'),
      ],
      icon: <Settings className="h-5 w-5" />,
    },
    {
      id: 5,
      title: t('routes.digitalTwins.quickStart.steps.4.title'),
      description: t('routes.digitalTwins.quickStart.steps.4.description'),
      action: t('routes.digitalTwins.quickStart.steps.4.action'),
      tips: [
        t('routes.digitalTwins.quickStart.steps.4.tips.0'),
        t('routes.digitalTwins.quickStart.steps.4.tips.1'),
        t('routes.digitalTwins.quickStart.steps.4.tips.2'),
      ],
      icon: <Eye className="h-5 w-5" />,
    },
  ];

  const currentStepData = quickStartSteps[currentStep - 1];

  return (
    <section className="py-16 bg-gradient-to-br from-purple-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('routes.digitalTwins.quickStart.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('routes.digitalTwins.quickStart.description')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Step Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">
                {t('routes.digitalTwins.quickStart.setupSteps')}
              </h3>
              <div className="space-y-4">
                {quickStartSteps.map((step) => (
                  <button
                    key={step.id}
                    onClick={() => setCurrentStep(step.id)}
                    className={`w-full text-left p-4 rounded-lg transition-all duration-200 ${
                      currentStep === step.id
                        ? "bg-purple-100 border-l-4 border-purple-500"
                        : currentStep > step.id
                        ? "bg-green-50 border-l-4 border-green-500"
                        : "bg-gray-50 hover:bg-gray-100"
                    }`}
                  >
                    <div className="flex items-center">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                          currentStep > step.id
                            ? "bg-green-500 text-white"
                            : currentStep === step.id
                            ? "bg-purple-500 text-white"
                            : "bg-gray-300 text-gray-600"
                        }`}
                      >
                        {currentStep > step.id ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : (
                          step.icon
                        )}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {step.title}
                        </h4>
                        <p className="text-sm text-gray-500 mt-1">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Progress */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>{t('routes.digitalTwins.quickStart.progress')}</span>
                  <span>
                    {Math.round((currentStep / quickStartSteps.length) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${(currentStep / quickStartSteps.length) * 100}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Step Content */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold">
                      {t('routes.digitalTwins.quickStart.stepLabel', { step: currentStep })}: {currentStepData.title}
                    </h3>
                    <p className="text-purple-100 mt-2">
                      {currentStepData.description}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold">{currentStep}</div>
                    <div className="text-sm text-purple-200">
                      {t('routes.digitalTwins.quickStart.of', { total: quickStartSteps.length })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Action */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">
                    {t('routes.digitalTwins.quickStart.whatToDo')}
                  </h4>
                  <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg">
                    <p className="text-gray-700">{currentStepData.action}</p>
                  </div>
                </div>

                {/* Tips */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">
                    {t('routes.digitalTwins.quickStart.proTips')}
                  </h4>
                  <ul className="space-y-2">
                    {currentStepData.tips.map((tip, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Navigation */}
                <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                    disabled={currentStep === 1}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {t('routes.digitalTwins.quickStart.previous')}
                  </button>

                  <div className="flex space-x-3">
                    {currentStep < quickStartSteps.length ? (
                      <button
                        onClick={() => setCurrentStep(currentStep + 1)}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        {t('routes.digitalTwins.quickStart.next')}
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </button>
                    ) : (
                      <button className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors">
                        {t('routes.digitalTwins.quickStart.complete')}
                        <CheckCircle className="h-4 w-4 ml-1" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
